/**
 * Continue your archive — foundation links below workspace pillars.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ChevronRight, GitBranch, HelpCircle, User } from 'lucide-react';
import { treeHref, personHref } from '../../utils/prettyArchiveLinks';
import { legacyBookPreviewHrefFromId } from '../../utils/legacyBookPreview';
import { CORE_JOURNEY_UI } from '../../config/testerRelease';

function ArchiveLink({ to, icon: Icon, label, testId, onAction }) {
  const className =
    'flex items-center justify-between gap-3 rounded-xl border border-[hsl(38_16%_82%)]/70 bg-[#fffdf8]/90 px-4 py-3 text-sm text-[#5c534a] transition hover:border-[hsl(38_14%_78%)] hover:bg-[hsl(42_28%_99%)] hover:text-[#1c1917]';

  if (onAction) {
    return (
      <button type="button" onClick={onAction} className={`${className} w-full text-left`} data-testid={testId}>
        <span className="flex min-w-0 items-center gap-2.5">
          <Icon className="h-4 w-4 shrink-0 text-[#8b7355]/80" aria-hidden />
          <span>{label}</span>
        </span>
        <ChevronRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
      </button>
    );
  }

  return (
    <Link to={to} className={className} data-testid={testId}>
      <span className="flex min-w-0 items-center gap-2.5">
        <Icon className="h-4 w-4 shrink-0 text-[#8b7355]/80" aria-hidden />
        <span>{label}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
    </Link>
  );
}

export function DashboardContinueArchiveSection({
  firstTree,
  myArchivePersonId,
  onCreateArchive,
}) {
  const peopleHref = firstTree ? `${treeHref(firstTree)}?tab=grid` : null;
  const profileHref = myArchivePersonId ? `${personHref({ id: myArchivePersonId })}?view=overview` : null;
  const legacyBookHref = myArchivePersonId ? legacyBookPreviewHrefFromId(myArchivePersonId) : null;

  return (
    <section
      className="rounded-2xl border border-[hsl(38_16%_82%)]/70 bg-[hsl(42_28%_99%)]/80 px-5 py-5 sm:px-6"
      data-testid="dashboard-continue-archive"
      aria-labelledby="continue-archive-heading"
    >
      <div className="mb-4 space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8b7355]">
          {CORE_JOURNEY_UI ? 'Quick links' : 'Archive foundation'}
        </p>
        <h2 id="continue-archive-heading" className="font-serif text-lg font-light text-[#1c1917] sm:text-xl">
          {CORE_JOURNEY_UI ? 'Continue where you left off' : 'Continue your archive'}
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {peopleHref ? (
          <ArchiveLink to={peopleHref} icon={GitBranch} label={CORE_JOURNEY_UI ? 'Add a loved one' : 'Open People'} testId="continue-open-people" />
        ) : (
          <ArchiveLink
            icon={GitBranch}
            label="Open People — create an archive first"
            testId="continue-open-people"
            onAction={onCreateArchive}
          />
        )}

        {profileHref ? (
          <ArchiveLink
            to={profileHref}
            icon={User}
            label="Continue latest profile"
            testId="continue-latest-profile"
          />
        ) : null}

        {legacyBookHref ? (
          <ArchiveLink
            to={legacyBookHref}
            icon={BookOpen}
            label="Preview Legacy Book"
            testId="continue-legacy-book-preview"
          />
        ) : null}

        <ArchiveLink to="/help" icon={HelpCircle} label="Open Help & Guides" testId="continue-open-help" />
      </div>
    </section>
  );
}

export default DashboardContinueArchiveSection;
